<template>
  <div class="payment-link-status-badge">
    <Tag
      v-if="status"
      :value="statusConfig.label"
      :severity="statusConfig.severity"
      :icon="statusConfig.icon"
      class="status-tag"
    />

    <!-- Payment link details tooltip -->
    <div v-if="paymentLink && showDetails" class="payment-link-details">
      <div v-if="paymentLink.amount" class="detail-item">
        <span class="detail-label">{{ t('Valor') }}:</span>
        <span class="detail-value">{{ formatAmountCents(paymentLink.amount) }}</span>
      </div>
      <div v-if="paymentLink.due_date" class="detail-item">
        <span class="detail-label">{{ t('Vencimento') }}:</span>
        <span class="detail-value">{{ formatDate(paymentLink.due_date) }}</span>
      </div>
      <div v-if="(paymentLink.installments ?? 0) > 1" class="detail-item">
        <span class="detail-label">{{ t('Parcelas') }}:</span>
        <span class="detail-value">{{ paymentLink.installments }}x</span>
      </div>
      <div v-if="paymentLink.payment_method !== 'any'" class="detail-item">
        <span class="detail-label">{{ t('Forma') }}:</span>
        <span class="detail-value">{{ getPaymentMethodLabel(paymentLink.payment_method) }}</span>
      </div>
    </div>

    <!-- Action buttons -->
    <div v-if="showActions" class="action-buttons">
      <Button
        v-if="canCopy"
        data-testid="payment-links-status-copy-button"
        icon="pi pi-copy"
        class="p-button-rounded p-button-text p-button-sm"
        v-tooltip.top="t('Copiar link')"
        @click="handleCopyLink"
      />
      <Button
        v-if="canSendWhatsApp"
        data-testid="payment-links-status-whatsapp-button"
        icon="pi pi-whatsapp"
        class="p-button-rounded p-button-text p-button-sm p-button-success"
        v-tooltip.top="t('Enviar via WhatsApp')"
        @click="handleSendWhatsApp"
      />
      <Button
        v-if="canCancel"
        data-testid="payment-links-status-cancel-button"
        icon="pi pi-times"
        class="p-button-rounded p-button-text p-button-sm p-button-danger"
        v-tooltip.top="t('Cancelar link')"
        @click="handleCancel"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { usePaymentLinks, STATUS } from '../../composables/usePaymentLinks';
import { useDateFormatter } from '../../composables/useDateFormatter';

interface PaymentLink {
  id?: number | string;
  status?: string;
  amount?: number;
  due_date?: string | null;
  installments?: number;
  payment_method?: string;
  url?: string;
  [key: string]: unknown;
}

interface StatusConfig {
  label: string;
  severity: string;
  icon: string;
}

const props = withDefaults(
  defineProps<{
    status?: string | null;
    paymentLink?: PaymentLink | null;
    showDetails?: boolean;
    showActions?: boolean;
  }>(),
  {
    status: null,
    paymentLink: null,
    showDetails: false,
    showActions: false,
  }
);

const emit = defineEmits<{
  (e: 'copy', paymentLink: PaymentLink): void;
  (e: 'send-whatsapp', paymentLink: PaymentLink): void;
  (e: 'cancel', paymentLink: PaymentLink): void;
}>();

const { t } = useI18n();
const { getLinkStatusConfig, formatAmountCents, canCancel: checkCanCancel } = usePaymentLinks();
const { formatDate: formatDateLocale } = useDateFormatter();

// Computed
const statusConfig = computed<StatusConfig>(() => {
  return getLinkStatusConfig(props.status || props.paymentLink?.status) as StatusConfig;
});

const canCopy = computed<boolean>(() => {
  const link = props.paymentLink;
  return !!(link?.url && link?.status === STATUS.ACTIVE);
});

const canSendWhatsApp = computed<boolean>(() => {
  const link = props.paymentLink;
  return !!(link?.url && link?.status === STATUS.ACTIVE);
});

const canCancel = computed<boolean>(() => {
  return !!props.paymentLink && checkCanCancel(props.paymentLink);
});

// Methods
const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return formatDateLocale(date);
};

const getPaymentMethodLabel = (method?: string): string => {
  const labels: Record<string, string> = {
    any: t('Qualquer'),
    pix: 'PIX',
    boleto: 'Boleto',
    credit_card: t('Cartão de Crédito')
  };
  return labels[method || ''] || method || '';
};

const handleCopyLink = async (): Promise<void> => {
  if (!props.paymentLink?.url) return;

  log('PaymentLinksStatusBadge', 'handleCopyLink', 'Copying link', props.paymentLink.url);

  try {
    await navigator.clipboard.writeText(props.paymentLink.url);
    emit('copy', props.paymentLink);
  } catch (err) {
    log('PaymentLinksStatusBadge', 'handleCopyLink', 'Error', err);
  }
};

const handleSendWhatsApp = (): void => {
  if (!props.paymentLink?.url) return;

  log('PaymentLinksStatusBadge', 'handleSendWhatsApp', 'Opening WhatsApp', props.paymentLink.url);
  emit('send-whatsapp', props.paymentLink);
};

const handleCancel = (): void => {
  if (!props.paymentLink?.id) return;

  log('PaymentLinksStatusBadge', 'handleCancel', 'Cancel requested', props.paymentLink.id);
  emit('cancel', props.paymentLink);
};
</script>

<style scoped>
.payment-link-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.status-tag {
  font-size: 0.75rem;
}

.payment-link-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem;
  background: var(--surface-ground);
  border-radius: 4px;
  font-size: 0.75rem;
}

.detail-item {
  display: flex;
  gap: 0.5rem;
}

.detail-label {
  color: var(--text-color-secondary);
}

.detail-value {
  font-weight: 500;
}

.action-buttons {
  display: flex;
  gap: 0.25rem;
}
</style>
